import { ApolloError } from '@apollo/client';
import { Button, CircularProgress } from '@shared/components';
import { useLoadInitialValues } from '@shared/components/react-hook-form';
import { Text } from '@shared/components/text';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  Product,
  useUpdateUserFeaturesMutation,
} from '@shared/generated/graphql';
import { User } from '@shared/types/user';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import clsx from 'clsx';
import { ReactElement } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import EmailScanningFeatureForm from './EmailScanning';
import EmailSubscriptions from './EmailSubscriptions';
import {
  EmailScanningFeatureFormValues,
  getInitialEmailScanningFormData,
  getUpdatedFeatures,
} from './utils/featuresForm';

export type UserFeaturesFormProps = StyleProps & {
  isLoading: boolean;
  onDiscard: () => void;
  organizationId: string;
  user?: User;
};

const ROOT = makeRootClassName('UserFeaturesForm');
const el = makeElementClassNameFactory(ROOT);

export function UserFeaturesForm(props: UserFeaturesFormProps): ReactElement {
  const [updateUserFeatures, { loading }] = useUpdateUserFeaturesMutation();
  const { sendToast } = useToast();
  const { me } = useMeContext();

  const organization = me?.organizationsDetails?.find(
    (org) => org.id === props.organizationId
  );

  const hasEmailScanningProduct = organization?.products?.includes(
    Product.EMAIL_SCANNING
  );

  const initialEmailScanningFormData = getInitialEmailScanningFormData(
    props.organizationId,
    props.user
  );

  const { control, reset, handleSubmit } =
    useForm<EmailScanningFeatureFormValues>({
      defaultValues: initialEmailScanningFormData,
    });
  useLoadInitialValues(reset, initialEmailScanningFormData);

  const handleError = (message?: string) => {
    sendToast('Failed to update feature.', {
      description: message,
      variant: 'error',
    });
  };

  const submitHandler: SubmitHandler<EmailScanningFeatureFormValues> = async (
    data
  ) => {
    if (!props.user?.id) return;

    const updatedFeatures = getUpdatedFeatures(
      data,
      props.organizationId,
      props.user
    );

    try {
      const res = await updateUserFeatures({
        variables: {
          input: {
            id: props.user?.id,
            features: {
              features: updatedFeatures,
            },
          },
        },
        refetchQueries: ['UserEmailSubscriptions', 'User'],
      });
      if (
        res.data?.updateUserFeatures.user &&
        res.data?.updateUserFeatures.success
      ) {
        sendToast('Feature updated!', { variant: 'success' });
        props.onDiscard();
      } else {
        handleError();
      }
    } catch (e) {
      const message = (e as ApolloError).message;
      handleError(message);
    }
  };

  return (
    <div className={clsx(ROOT, props.className)}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className={el`user-features`}>
          <Text type="body-md" isHeavy>
            Features
          </Text>
        </div>
        {hasEmailScanningProduct && (
          <EmailScanningFeatureForm el={el} control={control} />
        )}
        {props.user ? <EmailSubscriptions el={el} user={props.user} /> : null}
        <div className={el`footer`}>
          <Button variant="secondary" onPress={props.onDiscard} type="button">
            Discard
          </Button>
          <Button
            variant="primary"
            type="submit"
            isDisabled={loading || props.isLoading}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
}

type UserFeaturesFormContainerProps = StyleProps & {
  onDiscard(): void;
  user?: User;
  organizationId: string;
};

export default function UserFeaturesFormContainer({
  onDiscard,
  organizationId,
  user,
  ...styleProps
}: UserFeaturesFormContainerProps) {
  const isLoading = false;
  const isUpdating = false;
  return (
    <>
      {isLoading ? (
        <div className="flex w-full justify-center">
          <CircularProgress className="my-[300px]" />
        </div>
      ) : (
        <UserFeaturesForm
          isLoading={isUpdating}
          organizationId={organizationId}
          onDiscard={onDiscard}
          user={user}
          {...styleProps}
        />
      )}
    </>
  );
}
